<template>
  <div v-editable="props.blok" class="wp-media" :data-uid="props.blok._uid">
    <PflegeMedia :slides="props.blok.asset">
      <template #default="{ slide }: { slide: WPElementImageStoryblok | VideoStoryblok }">
        <div v-if="slide.component === 'WPElementImage'">
          <NuxtImg
            format="webp"
            sizes="sm:100vw md:810px xl:1170px"
            placeholder
            :src="slide.content.filename"
            :alt="slide.content.alt"
            :title="slide.content.title"
            :data-copyright="slide.content.copyright"
            :modifiers="{ smart: true }"
            quality="90"
            fit="in"
            loading="lazy"
            :style="{
              '--image-aspect-ratio-desktop': aspectRatioVariants.desktop,
              '--image-aspect-ratio-tablet': aspectRatioVariants.tablet,
              '--image-aspect-ratio-mobile': aspectRatioVariants.mobile,
              'border-radius': props.blok.roundCorners ? '10px' : '',
              ...getFocalPercent(slide.content)
            }"
          />
        </div>
        <div v-else-if="slide.component === 'Video'">
          <video
            ref="video"
            controls
            :src="slide.video?.filename ?? undefined"
            :poster="slide.thumbnail!.filename ?? undefined"
            :title="slide.video!.title ?? undefined"
          >
            Sorry, your browser does not support embedded videos.
          </video>
        </div>
      </template>
    </PflegeMedia>
  </div>
</template>

<script setup lang="ts">
import { PflegeMedia } from '@webcarelbj/shared-components'
import { useTracking } from '../..'
import { AspectRatio, AssetStoryblok, VideoStoryblok, WPElementImageStoryblok } from '../../types/assets'
import { parseFocus, sizeFromImageFilename } from '../../utils/assets'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { BlokBase } from '../../types/blok'

const { trackVideo, trackView } = useTracking()

export interface WPElementMediaBlok extends BlokBase {
  asset: (WPElementImageStoryblok | VideoStoryblok)[]
  roundCorners?: boolean
  component: 'WPElementMedia'
  _uid: string
}

const props = defineProps<{
  blok: WPElementMediaBlok
  aspectRatio: AspectRatio | string
}>()

const video = ref<HTMLVideoElement>()
const videoDuration = ref(0)
const videoDurationProgress = ref(0)
const setDurationProgress = ref()
const videoInteraction = reactive({
  video_percent: '0%',
  video_progress: '',
  video_title: '',
})

const aspectRatioVariants = computed(() => {
  if (typeof props.aspectRatio === 'object') {
    return props.aspectRatio
  } else if (typeof props.aspectRatio === 'string') {
    return {
      desktop: props.aspectRatio,
      tablet: props.aspectRatio,
      mobile: props.aspectRatio,
    }
  } else {
    return {
      desktop: 'initial',
      tablet: 'initial',
      mobile: 'initial',
    }
  }
})

const getFocalPercent = (content: AssetStoryblok) => {
  if (!content.focus || !content.filename) {
    return null
  }

  const { width, height } = sizeFromImageFilename(content.filename)
  const { x, y } = parseFocus(content.focus)
  const imageXPosition = (x * 100) / width
  const imageYPosition = (y * 100) / height

  return {
    '--image-x-position': imageXPosition + '%',
    '--image-y-position': imageYPosition + '%',
  }
}

const handleVideoPlayInteraction = () => {
  const delay = ((video.value!.duration - video.value!.currentTime) / 10) * 1000

  setDurationProgress.value = setInterval(() => {
    videoDurationProgress.value = (video.value!.currentTime * 100) / videoDuration.value
    videoInteraction.video_percent = Math.round(videoDurationProgress.value / 10) * 10 + '%'
  }, delay)

  videoInteraction.video_title = video.value!.title
  videoDuration.value = video.value!.duration
  videoInteraction.video_progress = 'play'
}

const handleVideoPauseInteraction = () => {
  videoInteraction.video_progress = 'pause'
  clearInterval(setDurationProgress.value)
}

const handleVideoEndedInteraction = () => {
  videoInteraction.video_progress = 'end'
  videoInteraction.video_percent = '100%'
  clearInterval(setDurationProgress.value)
}

const trackVideoInViewport = (target: HTMLVideoElement) => {
  const isInViewportThreshold = 2000 // video must be visible 2sec in viewport
  let isInViewportTimer: ReturnType<typeof setTimeout>

  const options = {
    root: null, // viewport = root-element
    rootMargin: '0px',
    threshold: 0.5, // 50% must be visible
  }

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        isInViewportTimer = setTimeout(() => {
          trackView({
            view_type: 'view_video',
            label: video.value!.title,
          })
          observer.unobserve(target)
        }, isInViewportThreshold)
      } else {
        clearTimeout(isInViewportTimer)
      }
    })
  }, options)
  observer.observe(target)
}

onMounted(() => {
  if (video.value) {
    video.value.addEventListener('play', () => handleVideoPlayInteraction())
    video.value.addEventListener('pause', () => handleVideoPauseInteraction())
    video.value.addEventListener('ended', () => handleVideoEndedInteraction())
    trackVideoInViewport(video.value)
  }
})

onBeforeUnmount(() => {
  if (video.value) {
    video.value.removeEventListener('play', () => handleVideoPlayInteraction)
    video.value.removeEventListener('pause', () => handleVideoPauseInteraction)
    video.value.removeEventListener('ended', () => handleVideoEndedInteraction)
  }
})

watch(videoInteraction, (newValue) => {
  if (newValue.video_percent !== '100%' && newValue.video_progress !== 'end') {
    trackVideo(newValue)
  } else if (newValue.video_progress === 'end') {
    trackVideo(newValue)
  }
})
</script>

<style scoped lang="scss">
:is(img, video, svg) {
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: var(--image-x-position) var(--image-y-position);
  aspect-ratio: var(--image-aspect-ratio-desktop, unset);

  @media (width <= 810px) {
    aspect-ratio: var(--image-aspect-ratio-tablet, var(--image-aspect-ratio-desktop));
  }

  @media (width <= 576px) {
    aspect-ratio: var(
      --image-aspect-ratio-mobile,
      var(--image-aspect-ratio-tablet, var(--image-aspect-ratio-desktop))
    );
  }
}
</style>
